'use-client';
import React, { useEffect } from 'react';
import type { IStylableButtonImperativeActions } from '../../../../../StylableButton/StylableButton.types';
import StylableButton from '../../../../../StylableButton/viewer/skinComps/StylableButton/StylableButton.skin';
import type { ISlideshowButtonProps } from '../../../../types';
import { useSlideshowContext } from '../../../../SlideshowContext';
import classNames from 'classnames';
import styles from './SlideshowButton.module.css';

const SlideshowButton: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  ISlideshowButtonProps
> = (
  {
    action,
    isDisabled,
    isDisabledByCtx,
    onDisabledChange,
    translations,
    a11y,
    onClick: propsOnClick,
    onFocus: propsOnFocus,
    onBlur: propsOnBlur,
    ...props
  },
  ref,
) => {
  const { onButtonClick, isButtonDisabled, setIsButtonFocused } =
    useSlideshowContext();
  const disabled = isDisabled ?? isDisabledByCtx;

  const onClick = (event: React.MouseEvent) => {
    onButtonClick(action);
    !disabled && propsOnClick?.(event);
  };

  const onFocus = (event: React.FocusEvent) => {
    setIsButtonFocused(true);
    !disabled && propsOnFocus?.(event);
  };

  const onBlur = (event: React.FocusEvent) => {
    setIsButtonFocused(false);
    !disabled && propsOnBlur?.(event);
  };

  const defaultLabel = {
    next: translations.ariaLabelNext,
    prev: translations.ariaLabelPrev,
  };

  useEffect(() => {
    const uxDisabled = isButtonDisabled(action);
    if (uxDisabled !== isDisabledByCtx) {
      onDisabledChange(uxDisabled);
    }
  }, [isDisabledByCtx, isButtonDisabled, action, onDisabledChange]);

  const updatedA11y = {
    ...a11y,
    label: a11y.label || props?.label || defaultLabel[action],
  };

  const className = classNames(
    styles.root,
    disabled ? styles.disabled : '',
    ...(props.customClassNames || []),
  );

  return (
    <StylableButton
      {...props}
      ref={ref}
      className={className}
      onClick={onClick}
      a11y={updatedA11y}
      isDisabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

export default React.forwardRef(SlideshowButton);
